import React from 'react';
import { PiCheck } from 'react-icons/pi';
import AnimateIn from '../components/AnimateIn';
import ContactForm from '../components/ContactForm';
import PhpSecurityAdvisory from '../components/PhpSecurityAdvisory';
import { COMPANY } from '../config/company';

const INCLUDED = [
  'Basic website security and configuration review',
  'PHP version and relevant security update checks, where access permits',
  'WordPress, plugin and theme checks, where applicable',
  'Mobile usability and responsive layout',
  'Basic website performance and loading checks',
  'Contact forms, links and booking journey checks',
  'A clear report explaining findings and prioritised recommendations',
];

const STEPS = [
  'Tell us about your website.',
  'We confirm the review scope and any access requirements.',
  'We carry out the agreed checks and prepare a report.',
  'You receive prioritised recommendations and decide what to do next.',
];

const FAQS = [
  {
    question: 'Is this service only for WordPress websites?',
    answer: 'No. Many small-business websites use WordPress, so we include plugin and theme checks where they apply. We also review other sites, including those built with PHP or other technologies. The exact checks depend on how the site is built and the access you provide.',
  },
  {
    question: 'Do you need access to my website?',
    answer: 'Not always. Some checks can be completed from the outside. Others — such as PHP version or plugin updates — may need access that you choose to provide. We confirm the scope and any access requirements first. Please do not send passwords through this form.',
  },
  {
    question: 'Will you fix problems you discover?',
    answer: 'The £79 check is a review and a report. We do not make changes as part of this service. If you would like help fixing issues afterwards, we can quote for that work separately.',
  },
  {
    question: 'Do I need an ongoing maintenance plan?',
    answer: 'No. This is a one-off check with no subscription. You can decide later whether you want help with updates, fixes or further website work.',
  },
  {
    question: 'Does the review guarantee my website is secure?',
    answer: 'No. This is a practical website health and configuration review. It is not a penetration test, a comprehensive vulnerability assessment, or an incident-response service. It helps you see what needs attention so you can decide what to do next.',
  },
];

const WebsiteHealthCheck = () => (
  <>
    <section className="bg-[#fffaf2] px-6 pb-16 pt-12 text-ink md:px-12 md:pb-20 md:pt-16 lg:px-16">
      <div className="mx-auto max-w-[46rem]">
        <AnimateIn immediate variant="fade-up">
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-violet-dark">Website services</p>
          <div className="mt-4 h-1 w-16 bg-violet" />
          <h1 className="mt-8 text-5xl font-semibold leading-[0.95] tracking-[-0.055em] md:text-6xl">
            Is your business website working as it should?
          </h1>
          <p className="mt-6 text-base leading-7 text-ink/70 md:text-lg md:leading-8">
            A website can look fine while running outdated software, containing broken contact forms or making it difficult for customers to get in touch. Our Website Health &amp; Security Check helps you understand what needs attention.
          </p>
        </AnimateIn>
        <AnimateIn variant="fade-up" delay={80} className="mt-10">
          <PhpSecurityAdvisory />
        </AnimateIn>
      </div>
    </section>

    <section className="bg-white px-6 py-16 text-ink md:px-12 lg:px-16 lg:py-20" aria-labelledby="included-heading">
      <div className="mx-auto max-w-[46rem]">
        <h2 id="included-heading" className="text-3xl font-semibold tracking-[-0.04em] md:text-4xl">What’s included</h2>
        <p className="mt-4 text-base leading-7 text-ink/70">
          We complete the checks that your website and the access you provide allow. Some findings can be reviewed externally; others need access you choose to share.
        </p>
        <ul className="mt-8 space-y-4">
          {INCLUDED.map((item) => (
            <li key={item} className="flex items-start gap-3 text-base leading-7 text-ink/80">
              <PiCheck className="mt-1 shrink-0 text-lg text-green" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section className="bg-[#f1efff] px-6 py-16 text-ink md:px-12 lg:px-16 lg:py-20" aria-labelledby="pricing-heading">
      <div className="mx-auto max-w-[46rem]">
        <h2 id="pricing-heading" className="text-sm font-bold uppercase tracking-[0.16em] text-violet-dark">Pricing</h2>
        <p className="mt-6 font-serif text-4xl font-medium leading-tight tracking-[-0.03em] md:text-5xl">
          £79 — One-off introductory website health check
        </p>
        <p className="mt-5 max-w-xl text-base leading-7 text-ink/70">
          No subscription required. Fixes, software licences, hosting charges and other additional work are quoted separately.
        </p>
      </div>
    </section>

    <section className="bg-[#fffaf2] px-6 py-16 text-ink md:px-12 lg:px-16 lg:py-20" aria-labelledby="process-heading">
      <div className="mx-auto max-w-[46rem]">
        <h2 id="process-heading" className="text-3xl font-semibold tracking-[-0.04em] md:text-4xl">Process</h2>
        <ol className="mt-10 space-y-6">
          {STEPS.map((step, index) => (
            <li key={step} className="flex items-start gap-4">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#031a3d] text-sm font-semibold text-white">
                {index + 1}
              </span>
              <p className="pt-1.5 text-base leading-7 text-ink/80">{step}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="bg-white px-6 py-16 text-ink md:px-12 lg:px-16 lg:py-20" aria-labelledby="faq-heading">
      <div className="mx-auto max-w-[46rem]">
        <h2 id="faq-heading" className="text-3xl font-semibold tracking-[-0.04em] md:text-4xl">Frequently asked questions</h2>
        <div className="mt-8">
          {FAQS.map(({ question, answer }) => (
            <details key={question} className="faq-item">
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#f7f5fd] px-6 py-16 text-ink md:px-12 lg:px-16 lg:py-20" aria-labelledby="health-check-contact-heading">
      <div className="mx-auto grid max-w-[78rem] items-start gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-violet-dark">Get in touch</p>
          <div className="mt-4 h-1 w-16 bg-violet" />
          <h2 id="health-check-contact-heading" className="mt-8 text-4xl font-semibold leading-[0.95] tracking-[-0.045em] md:text-5xl">
            Request a Website Health Check
          </h2>
          <p className="mt-6 max-w-md text-base leading-7 text-ink/65">
            Tell us about your website and how we can reach you. We will confirm the review scope before any work begins. Please do not send passwords through this form.
          </p>
          <p className="mt-6 text-sm text-ink/55">
            Prefer email?{' '}
            <a href={`mailto:${COMPANY.supportEmail}`} className="font-semibold text-violet-dark hover:text-magenta">
              {COMPANY.supportEmail}
            </a>
          </p>
        </div>
        <div className="rounded-[1.5rem] bg-white p-6 shadow-[0_18px_60px_rgba(4,11,65,.07)] sm:p-10">
          <ContactForm
            defaultType="website-health-check"
            heading="Send us a message"
            intro="Tell us about the public website you would like reviewed. All fields marked as required need completing."
          />
        </div>
      </div>
    </section>
  </>
);

export default WebsiteHealthCheck;
